import { createContextFactory } from '@mfw/components';
import React from 'react';

import type { UiTenant } from '../schemas/tenants.schema';

import { subscribeTenantOverrideChanged } from '../api/tenantOverride';
import { useTenantByDomain } from '../hooks/tenants';
import config from '../config';

export type TenantContext = {
  status: ReturnType<typeof useTenantByDomain>['status'];
  tenant?: UiTenant | null;
  isLoading: boolean;
};

const Context = createContextFactory({
  devToolsName: 'TenantContext',
  initialValue: {} as TenantContext,
});

export interface TenantProviderProps {
  children: React.ReactNode;
}
const TenantProvider = ({ children }: TenantProviderProps) => {
  const {
    data: tenant,
    isLoading,
    status,
  } = useTenantByDomain({
    domain: config.cloud.defaults.tenantDomain,
  });

  // A super-admin tenant override written in ANOTHER tab retargets this tab's
  // `X-Tenant-Id` on the very next request, while everything already rendered
  // — query caches included — still belongs to the previous tenant. The only
  // coherent reaction is the one the writing tab already performs on itself
  // (`TenantSwitcher`: write + reload): rebuild this tab under the new acting
  // tenant too. Reload rather than refetch, because the tenant is baked into
  // every cache entry's DATA but almost no cache entry's KEY.
  React.useEffect(
    () => subscribeTenantOverrideChanged(() => window.location.reload()),
    [],
  );

  const providedValue = React.useMemo(
    () => ({
      isLoading,
      status,
      tenant,
    }),
    [isLoading, status, tenant],
  );
  return <Context.Provider value={providedValue}>{children}</Context.Provider>;
};

export function useTenantContext() {
  return Context.useContext();
}
export default TenantProvider;
